import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { History, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export function RecentActivityWidget() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentLogs();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('recent_activity')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'user_activity_logs' },
        () => fetchRecentLogs()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchRecentLogs = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-activity-logs', {
        body: { limit: 5, offset: 0 },
      });

      if (error) throw error;
      setLogs(data?.data || []);
    } catch (error) {
      console.error('Error fetching recent logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryColors: Record<string, string> = {
    auth: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    profile: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    security: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    data: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    system: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Recent Activity
          </CardTitle>
          <CardDescription>Latest system events and changes</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/activity-logs">
            View All <ExternalLink className="ml-2 h-3 w-3" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={categoryColors[log.action_category] || categoryColors.system}>
                      {log.action_category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm">{log.description}</p>
                  {log.actor_profile && (
                    <p className="text-xs text-muted-foreground">
                      by {log.actor_profile.first_name} {log.actor_profile.last_name}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}